
import { useState, createContext, useContext, ReactNode, useEffect, useRef } from 'react';
import { subscribeCart, addCartItem as addCartItemSvc, updateCartItemQuantity as updateQtySvc, removeCartItem as removeItemSvc, clearUserCart as clearUserCartSvc, type CartItem as CartItemSvc } from '../services/cart';

interface CartItem {
  id: number | string; // mirrors listingId for Firestore-backed items
  listingId?: string;
  title: string;
  price: number;
  image?: string;
  quantity: number;
  sellerId?: string;
  sellerName?: string;
  metadata?: Record<string, any>;
}

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (item: Omit<CartItem, 'quantity'>, quantity?: number, metadata?: Record<string, any>) => void;
  removeFromCart: (id: number | string) => void;
  updateQuantity: (id: number | string, quantity: number) => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
  clearCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const userIdRef = useRef<string | null>(null);

  // subscribe to Firestore cart when user is available
  useEffect(() => {
    const userData = localStorage.getItem('campusMarketUser');
    if (!userData) return;
    const user = JSON.parse(userData);
    userIdRef.current = user.uid;
    const unsub = subscribeCart(user.uid, (items: CartItemSvc[]) => {
      setCartItems(items.map((it) => ({
        id: it.listingId,
        listingId: it.listingId,
        title: it.title,
        price: it.price,
        image: it.image,
        quantity: it.quantity,
        sellerId: it.sellerId,
        sellerName: it.sellerName,
        metadata: (it as any).metadata || undefined,
      })));
    });
    return () => unsub && unsub();
  }, []);

  const addToCart = (item: Omit<CartItem, 'quantity'>, quantity: number = 1, metadata?: Record<string, any>) => {
    // If not logged in, store locally
    if (!userIdRef.current) {
      setCartItems(prevItems => {
        const existingItem = prevItems.find(cartItem => cartItem.id === item.id && JSON.stringify(cartItem.metadata) === JSON.stringify(metadata || undefined));
        if (existingItem) {
          return prevItems.map(cartItem =>
            cartItem.id === item.id && JSON.stringify(cartItem.metadata) === JSON.stringify(metadata || undefined)
              ? { ...cartItem, quantity: cartItem.quantity + quantity }
              : cartItem
          );
        }
        return [...prevItems, { ...item, quantity, metadata }];
      });
      return;
    }
    // Firestore-backed cart
    const listingId = String(item.listingId || item.id);
    addCartItemSvc(userIdRef.current, {
      listingId,
      sellerId: item.sellerId || '',
      title: item.title || String((item as any).name || ''),
      price: item.price,
      image: item.image,
      sellerName: item.sellerName,
      metadata,
    }, quantity);
  };

  const removeFromCart = (id: number | string) => {
    if (!userIdRef.current) {
      setCartItems(prevItems => prevItems.filter(item => item.id !== id));
      return;
    }
    removeItemSvc(userIdRef.current, String(id));
  };

  const updateQuantity = (id: number | string, quantity: number) => {
    if (!userIdRef.current) {
      if (quantity <= 0) {
        removeFromCart(id);
        return;
      }
      setCartItems(prevItems =>
        prevItems.map(item =>
          item.id === id ? { ...item, quantity } : item
        )
      );
      return;
    }
    updateQtySvc(userIdRef.current, String(id), quantity);
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const clearCart = () => {
    if (!userIdRef.current) {
      setCartItems([]);
      return;
    }
    clearUserCartSvc(userIdRef.current);
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      addToCart,
      removeFromCart,
      updateQuantity,
      getTotalItems,
      getTotalPrice,
      clearCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

export { CartContext };
